import * as THREE from 'three'
import * as PostProcessing from 'postprocessing'
import { useRenderer } from './useRenderer'
import { useCamera } from './useCamera'
import { useControls } from './useControls'
import { ModelControls } from '../utils/ModelControls'
import { setupGLTFJewel } from '../setup-jewel'
import { useLights } from '../useLights'
import { fitCameraToObject } from '../utils/fit-camera-to-object'
import { IPostEffects, setupPostprocessing } from '../posteffects/posteffects'
import { isMobileOrTablet } from '../../../../trillion-common/src/utils/device-utils'
import { CameraController } from '../cameraController'
import { useAppStore } from '../../shared/store/store'
import { useAutoRotate } from './useAutoRotate'
import { useEffect, useState } from 'react'
import { useGUI } from './useGUI'
import { usePerformance } from './usePerformance'
import { useCubeMap } from './useCubeMap'
import { ViewerConfig } from '../../../../trillion-common/src/objects/viewer-config.object'
import { CameraConstraints } from '../../../../trillion-common/src/objects/camera-constraints.object'
import { PosteffectsConfigDto } from '../../../../trillion-common/src/dto/posteffects-config.dto'
import { parseDemoRingV2 } from '../utils/parse-demo-ring-v2'

interface IJewelViewerParams {
  rootContainer?: HTMLElement | undefined
  viewerConfig: ViewerConfig | undefined
  startCameraPosition: THREE.Vector3
  cancelMirroring: boolean | undefined
}

export function useJewelViewer({
  rootContainer,
  viewerConfig,
  startCameraPosition,
  cancelMirroring,
}: IJewelViewerParams) {
  const setCameraController = useAppStore((state) => state.setCameraController)
  const setDefaultCameraConstraints = useAppStore((state) => state.setDefaultCameraConstraints)
  const setIsLoading = useAppStore((state) => state.setIsLoading)
  const setIsStopped = useAppStore((state) => state.setIsStopped)
  const zoomMax = useAppStore((state) => state.zoomMax)
  const zoomMin = useAppStore((state) => state.zoomMin)

  const [jewelryModel, setJewelryModel] = useState<THREE.Object3D | undefined>(undefined)
  const [composer, setComposer] = useState<PostProcessing.EffectComposer | undefined>(undefined)
  const [mixer, setMixer] = useState<THREE.AnimationMixer | undefined>(undefined)
  const [scene] = useState(new THREE.Scene())
  const [postEffects, setPostEffects] = useState<IPostEffects | undefined>(undefined)
  const renderer = useRenderer()
  const camera = useCamera(startCameraPosition)
  const controls = useControls(camera, renderer?.domElement)
  const cameraController = useAppStore((state) => state.cameraController)
  const posteffectsConfigJson = useAppStore((state) => state.posteffectsConfigJson) ?? '{}'
  const reflectionMap = useAppStore((state) => state.reflectionMap)

  const cubemap = useCubeMap(viewerConfig, scene, reflectionMap?.reflectionMap)

  const lights = useLights(scene, viewerConfig)
  useGUI(
    jewelryModel,
    renderer,
    composer,
    postEffects,
    controls,
    scene,
    camera,
    lights,
    rootContainer,
    cubemap,
  )

  const stats = usePerformance(renderer, composer, postEffects, lights, rootContainer)

  useAutoRotate(renderer, controls)

  const isMobile = isMobileOrTablet()

  useEffect(() => {
    if (!renderer) return
    const _composer = composer ?? new PostProcessing.EffectComposer(renderer)
    if (!composer) setComposer(_composer)
    if (!postEffects && viewerConfig) {
      const posteffectsConfig = (
        posteffectsConfigJson !== '{}'
          ? JSON.parse(posteffectsConfigJson)
          : viewerConfig?.posteffectsConfig
      ) as PosteffectsConfigDto
      setPostEffects(
        setupPostprocessing(_composer, renderer, scene, camera, posteffectsConfig, false),
      )
    }
  }, [renderer, viewerConfig, posteffectsConfigJson])

  useEffect(() => {
    if (!controls) return
    setCameraController(CameraController(camera, controls))
  }, [controls])

  useEffect(() => {
    if (!renderer || !viewerConfig) return
    setupGLTFJewel(viewerConfig.modelPath, viewerConfig.category, renderer, cancelMirroring).then(
      (jewel) => {
        if (viewerConfig.sku === 'demo-ring-v2') {
          setJewelryModel(parseDemoRingV2(jewel))
        } else if (viewerConfig.sku === 'demo-necklace') {
          setJewelryModel(parseDemoRingV2(jewel))
        } else {
          setJewelryModel(jewel)
        }
      },
    )
  }, [viewerConfig, renderer])

  useEffect(() => {
    if (!jewelryModel) return
    if (!viewerConfig) throw new Error('viewerConfig is uninitialized')
    if (!postEffects) throw new Error('postEffects is uninitialized')
    if (!controls) throw new Error('controls is uninitialized')
    if (!cameraController) throw new Error('cameraController is uninitialized')

    if (controls && controls instanceof ModelControls) {
      const group = new THREE.Group()
      group.add(jewelryModel.parent ?? jewelryModel)
      scene.add(group)
      controls.attach(group)
    } else {
      scene.add(jewelryModel)
    }

    let _mixer: THREE.AnimationMixer | undefined = undefined
    jewelryModel.traverse((child) => {
      if (child.animations.length === 0) return
      _mixer = new THREE.AnimationMixer(child)
      child.animations.forEach((clip) => _mixer?.clipAction(clip).play())
    })

    setMixer(_mixer)

    postEffects.setupSelectiveBloom(jewelryModel)

    const fitOffset = isMobile ? 1.4 : 1.5
    const distance = fitCameraToObject(camera, controls, jewelryModel, fitOffset)

    setDefaultCameraConstraints(
      new CameraConstraints({
        maxDistance: distance * (zoomMax ? zoomMax : 3),
        minDistance: distance * (zoomMin ? zoomMin : 0.5),
      }),
    )

    if (viewerConfig.pointsOfView.length > 0) {
      const firstPoint = viewerConfig.pointsOfView[0]
      camera.position.copy(firstPoint.cameraPosition)
      if (firstPoint.lookAtPosition) controls.target.copy(firstPoint.lookAtPosition)
      cameraController.update()
    }

    setIsLoading(false)
    setIsStopped(false)

    return () => {
      scene.clear()
      setMixer(undefined)
    }
  }, [jewelryModel])

  return { model: jewelryModel, renderer, controls, composer, stats, mixer }
}
